import { useState } from "react";
import { useMessageStore } from "../store";
import {
  CurrentChatStatus,
  HeaderType,
  MessageSender,
  MessageStatus,
  MessageType,
  UploadFileInfo,
} from "../store/types";
import { v4 as uuidv4 } from "uuid";
import { fileUploadApi } from "../api/file";
import { webSocketService } from "../services/WebSocketService";
import { useFileUpload } from "./useFileUpload";

export interface ModalHandler {
  handleOk: () => void;
  handleCancel: () => void;
  getFile: () => string | undefined;
}

export const useFilePaste = () => {
  const [pasteFileVisible, setPasteFileVisible] = useState<boolean>(false);
  const [filePath, setFilePath] = useState<string>();
  const [file, setFile] = useState<File>();
  const { addOutgoingMessage, updateMessageStatus, chatStatus } =
    useMessageStore();
  const { onError } = useFileUpload();
  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    // 1. 获取剪贴板数据
    const clipboardItems = e.clipboardData.items;
    if (
      !clipboardItems ||
      !chatStatus ||
      chatStatus === CurrentChatStatus.ChatEnd
    )
      return;

    // 2. 查找图片类型数据
    for (const item of clipboardItems) {
      if (item.type.indexOf("image") !== 0) continue;

      // 3. 获取图片 Blob
      const blob = item.getAsFile();
      if (!blob) continue;

      // 4. 创建 File 对象
      const file = new File([blob], "pasted-image.png", {
        type: blob.type || "image/png",
      });

      setFile(file);

      // 5. 处理文件（示例：显示预览）
      const url = URL.createObjectURL(file);
      setFilePath(url);
      setPasteFileVisible(true);

      // 阻止默认粘贴行为（如果只需要图片）
      e.preventDefault();
      break;
    }
  };

  const modalHandler = {
    handleOk: async () => {
      const fileId = uuidv4();
      addOutgoingMessage({
        headerType: HeaderType.MESSAGE,
        clientId: fileId,
        content: `{"fileName":"", "fileUrl": "${filePath}", "fileId": ""}`,
        senderType: MessageSender.USER,
        type: MessageType.Image,
        timestamp: Date.now(),
        status: MessageStatus.Sending,
      });
      setPasteFileVisible(false);
      if (!file) {
        updateMessageStatus(fileId, { status: MessageStatus.ERROR });
        return;
      }
      // 发送服务端
      await fileUploadApi({
        file,
        fileId,
        onError: (uid: string, fileInfo: UploadFileInfo) => {
          onError(uid, fileInfo);
        },
        onSuccess: (data) => {
          // 通过WebSocket发送消息
          webSocketService.sendMessage(
            `{"fileName":"${data.sourceFileName}", "fileUrl": "${data.filePath}", "fileId": "${data.fileId}"}`,
            {
              msgType: MessageType.Image,
              noNeedInsertToMsgList: true,
              clientId: data.fileId,
            },
          );
        },
      });
    },
    handleCancel: () => {
      setPasteFileVisible(false);
    },
    getFile: () => filePath,
  };

  return {
    handlePaste,
    pasteFileVisible,
    modalHandler,
  };
};
